import { $http } from '@/bootstrap.js';
import { CurieIdScannerType } from '@/Components/CurieIdScanner.vue';
import { eventBus, events } from '@/eventBus.js';
import { trans } from '@/mixins/i18n.js';
import { visitModal } from '@/Utils/visitModal';
import * as Sentry from '@sentry/vue';
import { ref } from 'vue';
import { route } from 'ziggy-js';

export function useCurieIdScanner() {
    const isProcessing = ref(false);

    async function recordScan(curieId: string, method: 'qr' | 'manual' = 'qr'): Promise<void> {
        await $http.post(route('curie-ids.scan'), { curieId, method });
    }

    function openForAssetSearch(): void {
        eventBus.$emit(events.openQrCodeScanner, {
            type: CurieIdScannerType.searchAsset,
        });
    }

    function openForAttachingToAsset(assetId: string): void {
        eventBus.$emit(events.openQrCodeScanner, {
            assetId,
            type: CurieIdScannerType.attachCurieId,
        });
    }

    function openForScanningValue(): Promise<string> {
        return new Promise((resolve) => {
            eventBus.$emit(events.openQrCodeScanner, {
                type: CurieIdScannerType.scanId,
                onSuccessfulScan: (code: string, scanner: { close: () => void }) => {
                    scanner.close();
                    resolve(code);
                },
            });
        });
    }

    function runOpenTaskScanFlow(): Promise<{ curieIdToFilter: string } | null> {
        return new Promise((resolve) => {
            eventBus.$emit(events.openQrCodeScanner, {
                type: CurieIdScannerType.scanId,
                onSuccessfulScan: async (curieId: string, scanner: any) => {
                    isProcessing.value = true;

                    try {
                        const { data } = await $http.get(route('tasks.scan-curie-id', { curieId }));

                        // The asset is inoperable and this tenant forbids ad-hoc testing it
                        // (a passing test would flip it back to operable). Show a blocking
                        // modal so the operator immediately knows why nothing opened.
                        if (data?.blockedReason === 'inoperable') {
                            scanner.close();
                            eventBus.$emit(events.openInoperableTestBlockedModal);
                            resolve(null);
                            return;
                        }

                        // Inoperable / not-present assets short-circuit to an ad-hoc test —
                        // the operator is in front of the asset and needs to test it now,
                        // even if there's a queued scheduled task they'd otherwise pick up.
                        if (data?.adhocTestUrl) {
                            scanner.close();
                            visitModal(data.adhocTestUrl, { preserveScroll: true });
                            resolve(null);
                            return;
                        }

                        if (data?.count === 1 && data?.task?.url) {
                            scanner.close();
                            visitModal(data.task.url, { preserveScroll: true });
                            resolve(null);
                            return;
                        }

                        if (data?.asset?.curie_id) {
                            scanner.close();
                            resolve({ curieIdToFilter: data.asset.curie_id });
                            return;
                        }

                        scanner.isValidQrCode = false;
                        scanner.form.errors.curieId = trans('assets.overview.scanner.error.asset_not_found');
                        resolve(null);
                    } catch (error: any) {
                        scanner.isValidQrCode = false;

                        // A 404 is the only genuine "no asset" outcome. Anything else (network
                        // failure, server error) is a real fault — show a retryable message
                        // instead of "Tag not found", and flag it for the field.
                        if (error?.response?.status === 404) {
                            scanner.form.errors.curieId = trans('assets.overview.scanner.error.asset_not_found');
                        } else {
                            scanner.form.errors.curieId = trans('assets.overview.scanner.error.scan_failed');
                            Sentry.captureException(error);
                        }

                        resolve(null);
                    } finally {
                        isProcessing.value = false;
                    }
                },
            });
        });
    }

    return {
        recordScan,
        openForAssetSearch,
        openForAttachingToAsset,
        openForScanningValue,
        runOpenTaskScanFlow,
        isProcessing,
    };
}
